// hooks/useFormBeneficiary.ts
import { useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { agruparTitularesEDependentes } from '../utils/dataProcessing';

interface FormBeneficiaryReturn {
  selecionado: string;
  setSelecionado: (value: string) => void;
  excelFile: File | null;
  setExcelFile: (value: File | null) => void;
  data: any[];
  setData: (value: any[] | ((prev: any[]) => any[])) => void;
  departamentos: any[];
  departamento: string;
  setDepartamento: (value: string) => void;
  vigencia: string;
  setVigencia: (value: string) => void;
  searchNome: string;
  setSearchNome: (value: string) => void;
  searchCpf: string;
  setSearchCpf: (value: string) => void;
  hasPlanoOne: boolean;
  setHasPlanoOne: (value: boolean) => void;
  hasPlanoNow: boolean;
  planoOne: any;
  setPlanoOne: (value: any | ((prev: any) => any)) => void;
  planoNow: any;
  setPlanoNow: (value: any | ((prev: any) => any)) => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  submissionResponse: any;
  setSubmissionResponse: (value: any) => void;
  handleFileChange: (file: File) => void;
  fetchDepartamentos: () => void;
  selectedRows: string[];
  setSelectedRows: (value: string[] | ((prev: string[]) => string[])) => void;
  excelHeaders: string[]; // Headers originais do excel
  setExcelHeaders: (headers: string[]) => void;
  excelRawRows: any[][]; // Linhas originais do excel
  setExcelRawRows: (rows: any[][]) => void;
  failedRows: any[];
  setFailedRows: (rows: any[] | ((prev: any[]) => any[])) => void;
  selectedDependentes: { [key: string]: string[] };
  setSelectedDependentes: (
    value:
      | { [key: string]: string[] }
      | ((prev: { [key: string]: string[] }) => { [key: string]: string[] }),
  ) => void;
  expandedRows: string[];
  setExpandedRows: (value: string[] | ((prev: string[]) => string[])) => void;
  currentChunk: number;
  setCurrentChunk: (value: number) => void;
  processedItems: number;
  setProcessedItems: (value: number) => void;
  failedChunks: number[];
  setFailedChunks: (value: number[] | ((prev: number[]) => number[])) => void;
  successResponses: any[];
  setSuccessResponses: (value: any[] | ((prev: any[]) => any[])) => void;
  headerCheckboxRef: React.RefObject<HTMLInputElement>;
}

export const useFormBeneficiary = (): FormBeneficiaryReturn => {
  // Estados principais
  const [selecionado, setSelecionado] = useState('');
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [excelRawRows, setExcelRawRows] = useState<any[][]>([]);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [selectedDependentes, setSelectedDependentes] = useState<{
    [key: string]: string[];
  }>({});
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [failedRows, setFailedRows] = useState<any[]>([]);

  // Estados de planos
  const [hasPlanoOne, setHasPlanoOne] = useState(false);
  const [hasPlanoNow, setHasPlanoNow] = useState(false);
  const [planoOne, setPlanoOne] = useState({
    amparo: true,
    odontogroup: true,
    topmed: false,
  });
  const [planoNow, setPlanoNow] = useState({
    amparo: false,
    odontogroup: true,
    topmed: true,
  });

  // Estados de departamento e vigência
  const [departamentos, setDepartamentos] = useState([]);
  const [departamento, setDepartamento] = useState('');
  const [vigencia, setVigencia] = useState(
    new Date().toISOString().split('T')[0],
  );

  // Estados de busca
  const [searchNome, setSearchNome] = useState('');
  const [searchCpf, setSearchCpf] = useState('');

  // Estados de processamento
  const [loading, setLoading] = useState(false);
  const [submissionResponse, setSubmissionResponse] = useState<any>(null);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [processedItems, setProcessedItems] = useState(0);
  const [failedChunks, setFailedChunks] = useState<number[]>([]);
  const [successResponses, setSuccessResponses] = useState<any[]>([]);

  // Refs
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  // Função para processar arquivo Excel
  const handleFileChange = (file: File) => {
    if (!file) return;

    const validExtensions = ['.xlsx', '.xls'];
    const maxSizeMB = 5;
    const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
    const reader = new FileReader();

    if (!validExtensions.includes(fileExtension)) {
      toast.error('Formato inválido. Envie um arquivo .xls ou .xlsx.');
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Arquivo muito grande. Máximo permitido: ${maxSizeMB} MB.`);
      return;
    }

    reader.onload = event => {
      const data = new Uint8Array(event.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[2]
        ? workbook.SheetNames[2]
        : workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
      }) as any[][];

      const headers = jsonData[0];
      const rows = jsonData.slice(1);
      setExcelHeaders(headers as string[]);
      setExcelRawRows(rows);

      const formattedData = rows.map((row: any[]) =>
        (headers as string[]).reduce(
          (obj: any, header: string, index: number) => {
            obj[header] = row[index] || null;
            return obj;
          },
          {},
        ),
      );
      const processedData = agruparTitularesEDependentes(
        formattedData,
        setHasPlanoOne,
        setHasPlanoNow,
      );

      setData(processedData);
      setExcelFile(file);
    };

    reader.readAsArrayBuffer(file);
  };

  // Fetch departamentos
  const fetchDepartamentos = async () => {
    const urlBase = process.env.REACT_APP_APIV3_URL;
    const user = process.env.REACT_APP_APIV3_USER;
    const pass = process.env.REACT_APP_APIV3_SENHA;

    const getToken = async () => {
      const response = await axios.get(
        `${urlBase}/login?user=${user}&password=${pass}`,
      );
      return response.data.token;
    };

    const retorno = await axios.get(
      `${urlBase}/departamentos?empresa=${
        selecionado === 'empresarial' ? 27769 : 27768
      }`,
      {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      },
    );

    setDepartamentos(retorno.data);
  };

  return {
    // Estados
    selecionado,
    setSelecionado,
    excelFile,
    setExcelFile,
    data,
    setData,
    selectedRows,
    setSelectedRows,
    excelHeaders,
    setExcelHeaders,
    excelRawRows,
    setExcelRawRows,
    failedRows,
    setFailedRows,
    selectedDependentes,
    setSelectedDependentes,
    expandedRows,
    setExpandedRows,
    hasPlanoOne,
    setHasPlanoOne,
    hasPlanoNow,
    planoOne,
    setPlanoOne,
    planoNow,
    setPlanoNow,
    departamentos,
    departamento,
    setDepartamento,
    vigencia,
    setVigencia,
    searchNome,
    setSearchNome,
    searchCpf,
    setSearchCpf,
    loading,
    setLoading,
    submissionResponse,
    setSubmissionResponse,
    currentChunk,
    setCurrentChunk,
    processedItems,
    setProcessedItems,
    failedChunks,
    setFailedChunks,
    successResponses,
    setSuccessResponses,
    headerCheckboxRef,

    // Funções
    handleFileChange,
    fetchDepartamentos,
  };
};
